import styled from 'styled-components';
import { COLORS, UI_COLORS } from '../../../../constants/colors';
import { LAYOUT, BUTTON_SIZES, Z_INDEX, SLIDER_CONFIG } from '../../../../constants/uiConstants';

// Header Bar
export const HeaderBar = styled.div`
  height: ${LAYOUT.headerHeight}px;
  background: ${UI_COLORS.headerBackground};
  border-radius: ${LAYOUT.borderRadius.lg}px;
  display: flex;
  align-items: center;
  padding: 0 ${LAYOUT.spacing.xl}px;
  position: relative;
  margin-bottom: ${LAYOUT.spacing.xxxl}px;
  
  @media (max-width: ${LAYOUT.breakpoints.mobile}px) {
    padding: 0 ${LAYOUT.spacing.lg}px;
    margin-bottom: ${LAYOUT.spacing.xl}px;
  }
`;

export const HeaderGreeting = styled.div`
  font-weight: 600;
  color: ${UI_COLORS.headerText};
  font-size: ${LAYOUT.fontSize.xl};
  
  @media (max-width: ${LAYOUT.breakpoints.mobile}px) {
    font-size: ${LAYOUT.fontSize.lg};
  }
`;

export const HeaderStats = styled.div`
  position: absolute;
  right: 20px;
  display: flex;
  gap: 20px;
  
  @media (max-width: 768px) {
    right: 16px;
  }
`;

export const HeaderStatItem = styled.div`
  font-weight: 600;
  color: ${UI_COLORS.headerStatText};
  font-size: ${LAYOUT.fontSize.xl};
  
  @media (max-width: ${LAYOUT.breakpoints.mobile}px) {
    font-size: ${LAYOUT.fontSize.lg};
  }
`;

// Dashboard Wrapper
export const DashboardWrapper = styled.div`
  max-width: ${LAYOUT.maxWidth}px;
  margin: 0 auto;
  padding: ${LAYOUT.spacing.xl}px;
  background-color: ${UI_COLORS.dashboardBackground};
  min-height: calc(100vh - ${LAYOUT.headerHeight}px);
  
  @media (max-width: ${LAYOUT.breakpoints.mobile}px) {
    padding: ${LAYOUT.spacing.lg}px;
  }
`;

// Two Column Grid (updated from three column)
export const TwoColumnGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${LAYOUT.spacing.xxxl}px;
  margin-bottom: 40px;
  
  @media (max-width: ${LAYOUT.breakpoints.tablet}px) {
    grid-template-columns: 1fr;
    gap: ${LAYOUT.spacing.xl}px;
  }
`;

// Dashboard Grid - supports 3-4 columns
export const ThreeColumnGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(340px, 1fr));
  gap: ${LAYOUT.spacing.xxxl}px;
  margin-bottom: 40px;
  
  @media (max-width: 1400px) {
    grid-template-columns: repeat(auto-fit, minmax(320px, 1fr));
  }
  
  @media (max-width: ${LAYOUT.breakpoints.desktop}px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: ${LAYOUT.breakpoints.mobile}px) {
    grid-template-columns: 1fr;
    gap: ${LAYOUT.spacing.xl}px;
  }
`;

// Dashboard Column
export const DashboardColumn = styled.div<{ $expanded?: boolean }>`
  background: white;
  border-radius: ${LAYOUT.borderRadius.xxl}px;
  box-shadow: 0 10px 40px ${UI_COLORS.shadowColor};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  min-height: ${props => props.$expanded ? LAYOUT.expandedColumnHeight : LAYOUT.minColumnHeight}px;
  transition: transform ${SLIDER_CONFIG.transitionDuration} ease, box-shadow ${SLIDER_CONFIG.transitionDuration} ease;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 20px 60px ${UI_COLORS.heavyShadowColor};
  }
`;

// Column Header
export const ColumnHeader = styled.div`
  background: ${UI_COLORS.columnHeaderBackground};
  color: white;
  padding: ${LAYOUT.spacing.lg - 1}px ${LAYOUT.spacing.xl}px;
  display: flex;
  align-items: center;
  gap: ${LAYOUT.spacing.md - 2}px;
  font-weight: 600;
  font-size: ${LAYOUT.fontSize.xl};
`;

export const ColumnIcon = styled.span`
  font-size: ${LAYOUT.fontSize.xxl};
`;

export const ColumnTitle = styled.h2`
  font-size: ${LAYOUT.fontSize.xl};
  font-weight: 600;
  margin: 0;
`;

// Column Content
export const ColumnContent = styled.div`
  padding: ${LAYOUT.spacing.xxl + 5}px;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT.spacing.xl}px;
  overflow-x: hidden;
  overflow-y: auto;
  width: 100%;
  box-sizing: border-box;
  position: relative;
  
  @media (max-width: 768px) {
    padding: ${LAYOUT.spacing.lg}px;
    gap: ${LAYOUT.spacing.lg}px;
  }
`;

// Quick Stats
export const QuickStats = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
  gap: ${LAYOUT.spacing.lg - 1}px;
  margin-bottom: ${LAYOUT.spacing.xxl + 5}px;
`;

export const MiniStatCard = styled.div<{ $color: string }>`
  background: ${UI_COLORS.statCardBackground};
  padding: ${LAYOUT.spacing.xl}px;
  border-radius: ${LAYOUT.borderRadius.xl}px;
  text-align: center;
  border-left: ${LAYOUT.spacing.xs}px solid ${props => props.$color};
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-2px);
  }
`;

export const MiniStatValue = styled.div`
  font-size: ${LAYOUT.fontSize.xxxl};
  font-weight: 700;
  color: ${UI_COLORS.columnHeaderBackground};
  margin-bottom: 5px;
`;

export const MiniStatLabel = styled.div`
  font-size: ${LAYOUT.fontSize.md};
  color: ${UI_COLORS.textSecondary};
`;

// Quick Actions
export const QuickActions = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${LAYOUT.spacing.md - 2}px;
`;

export const QuickActionButton = styled.button<{ $outline?: boolean }>`
  background: ${props => props.$outline ? 'transparent' : COLORS.primary};
  color: ${props => props.$outline ? COLORS.primary : 'white'};
  border: ${props => props.$outline ? `2px solid ${COLORS.primary}` : 'none'};
  padding: ${LAYOUT.spacing.md - 2}px ${LAYOUT.spacing.lg}px;
  border-radius: ${LAYOUT.borderRadius.lg}px;
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: ${COLORS.primary};
    color: white;
  }
`;

// Section Components
export const SectionDivider = styled.div`
  height: 1px;
  background: #e2e8f0;
  margin: 20px 0;
`;

export const SectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  gap: 10px;
  flex-wrap: wrap;
  
  @media (max-width: 480px) {
    margin-bottom: 12px;
  }
`;

export const SectionTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
  flex: 1;
  min-width: 0;
  
  @media (max-width: 480px) {
    font-size: 14px;
  }
`;

// Search Section
export const SearchSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
`;

export const SearchInputWrapper = styled.div`
  display: flex;
  gap: 8px;
`;

export const SearchInput = styled.input`
  flex: 1;
  padding: 10px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
  
  &:focus {
    border-color: ${COLORS.primary};
  }
  
  &::placeholder {
    color: #94a3b8;
  }
`;

export const SearchIconButton = styled.button`
  background: ${COLORS.primary};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 0 16px;
  cursor: pointer;
  transition: all 0.2s;
  font-size: 16px;
  
  &:hover {
    background: ${COLORS.darkPurple};
  }
`;

export const PopularSubjects = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

export const SectionSubtitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #334155;
  margin: 0;
`;

export const SubjectTags = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;

export const SubjectTag = styled.button`
  background: #e0e7ff;
  color: #4338ca;
  border: none;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #4338ca;
    color: white;
  }
`;

export const SearchResultsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
`;

export const SearchResultsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const SearchResultItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s;
  
  &:hover {
    background: #f1f5f9;
    transform: translateX(4px);
  }
`;

export const ResultInfo = styled.div`
  flex: 1;
`;

export const ResultTitle = styled.h4`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 4px 0;
`;

export const ResultMeta = styled.p`
  font-size: 12px;
  color: #64748b;
  margin: 0;
`;

export const ResultPrice = styled.span`
  font-size: 16px;
  font-weight: 700;
  color: ${COLORS.primary};
`;

export const AdvancedSearchLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  background: ${UI_COLORS.statCardBackground};
  border: 2px solid ${COLORS.primary};
  border-radius: ${LAYOUT.borderRadius.lg}px;
  padding: ${LAYOUT.spacing.md}px ${LAYOUT.spacing.lg}px;
  color: ${COLORS.primary};
  font-size: ${LAYOUT.fontSize.md};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: auto;
  
  &:hover {
    background: ${COLORS.primary};
    color: white;
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

export const AdvancedSearchIcon = styled.span`
  font-size: ${LAYOUT.fontSize.lg};
  transition: transform 0.2s ease;
  
  ${AdvancedSearchLink}:hover & {
    transform: scale(1.1);
  }
`;

// Classes Section
export const ClassesScrollContainer = styled.div<{ $expanded?: boolean }>`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 15px;
  overflow: hidden;
  transition: all 0.3s ease-out;
`;

// Available Classes Grid (for non-slider view)
export const AvailableClassesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
  gap: 20px;
  width: 100%;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

// Slider Section Wrapper
export const SliderSection = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  gap: 16px;
  margin: 0 auto;
  position: relative;
  overflow-x: hidden;
  padding: 20px 10px;
  box-sizing: border-box;
  background: #fafbfc;
  border-radius: 12px;
  
  @media (max-width: 768px) {
    padding: 16px 5px;
    border-radius: 8px;
  }
`;

// New Slider Container
export const SliderContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  max-width: 380px;
  margin: 0 auto;
  padding: 0 40px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    max-width: calc(100vw - 80px);
    padding: 0 35px;
  }
  
  @media (max-width: 480px) {
    max-width: calc(100vw - 60px);
    padding: 0 30px;
  }
`;

// Slider Viewport (visible area)
export const SliderViewport = styled.div`
  overflow: hidden;
  width: 100%;
  max-width: 280px;
  touch-action: pan-y;
  user-select: none;
  -webkit-user-select: none;
  cursor: grab;
  
  &:active {
    cursor: grabbing;
  }
  
  @media (max-width: 480px) {
    max-width: 260px;
  }
`;

// Slider Track (contains all items)
export const SliderTrack = styled.div<{ $currentIndex: number; $isDragging?: boolean }>`
  display: flex;
  transition: ${props => props.$isDragging ? 'none' : 'transform 0.3s ease-in-out'};
  transform: translateX(${props => -props.$currentIndex * 100}%);
  cursor: ${props => props.$isDragging ? 'grabbing' : 'grab'};
`;

// Individual Slide Item
export const SlideItem = styled.div`
  min-width: 100%;
  width: 100%;
  flex-shrink: 0;
  display: flex;
  justify-content: center;
  align-items: center;
  padding: 0;
  box-sizing: border-box;
  
  & > div {
    width: 100%;
    max-width: 280px;
  }
  
  @media (max-width: 480px) {
    & > div {
      max-width: 260px;
    }
  }
`;

// Single Item Container (when only one class is available)
export const SingleItemContainer = styled.div`
  display: flex;
  justify-content: center;
  width: 100%;
  padding: 20px 10px;
  background: #fafbfc;
  border-radius: 12px;
  box-sizing: border-box;
  
  @media (max-width: 768px) {
    padding: 16px 5px;
    border-radius: 8px;
  }
`;

export const SingleItemWrapper = styled.div`
  width: 100%;
  max-width: 280px;
  display: flex;
  justify-content: center;
  
  @media (max-width: 480px) {
    max-width: 260px;
  }
`;

// Legacy exports for backward compatibility
export const ClassesSliderContainer = SliderContainer;
export const ClassesSliderWrapper = SliderTrack;
export const ClassSlide = SlideItem;
export const ClassItemWrapper = SlideItem;

// Side Arrow Button
export const SideArrowButton = styled.button<{ $position: 'left' | 'right'; disabled?: boolean }>`
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  ${props => props.$position === 'left' ? 'left: 0;' : 'right: 0;'}
  background: ${props => props.disabled ? '#f8fafc' : 'white'};
  color: ${props => props.disabled ? '#cbd5e1' : COLORS.primary};
  border: 1px solid ${props => props.disabled ? '#e2e8f0' : '#e2e8f0'};
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  font-size: 16px;
  z-index: 2;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.08);
  opacity: ${props => props.disabled ? 0.4 : 0.9};
  
  &:hover:not(:disabled) {
    background: ${COLORS.primary};
    color: white;
    opacity: 1;
    box-shadow: 0 4px 8px rgba(99, 102, 241, 0.2);
  }
  
  &:active:not(:disabled) {
    transform: translateY(-50%) scale(0.95);
  }
  
  @media (max-width: 768px) {
    width: 28px;
    height: 28px;
    font-size: 14px;
  }
`;

// Slider Navigation (keeping for backward compatibility)
export const SliderNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 16px;
  margin-top: 24px;
  padding-top: 20px;
  border-top: 1px solid #e2e8f0;
  position: relative;
  z-index: 1;
`;

export const SliderButton = styled.button<{ disabled?: boolean }>`
  background: ${props => props.disabled ? '#f1f5f9' : COLORS.primary};
  color: ${props => props.disabled ? '#cbd5e1' : 'white'};
  border: 1px solid ${props => props.disabled ? '#e2e8f0' : 'transparent'};
  width: 36px;
  height: 36px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s ease;
  font-size: 18px;
  
  &:hover:not(:disabled) {
    background: ${COLORS.darkPurple};
    transform: scale(1.1);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.25);
  }
  
  &:active:not(:disabled) {
    transform: scale(0.95);
  }
`;

export const SliderDots = styled.div`
  display: flex;
  gap: 6px;
  align-items: center;
  justify-content: center;
  margin-top: 8px;
  flex-wrap: wrap;
  max-width: 100%;
  padding: 0 10px;
`;

export const SliderDot = styled.button<{ $active?: boolean }>`
  width: ${props => props.$active ? '24px' : '8px'};
  height: 8px;
  border-radius: 4px;
  border: none;
  background: ${props => props.$active ? COLORS.primary : '#e2e8f0'};
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  padding: 0;
  
  &:hover {
    background: ${props => props.$active ? COLORS.primary : '#cbd5e1'};
    transform: ${props => props.$active ? 'none' : 'scale(1.2)'};
  }
`;

// Browse Classes Button (like Add Course button on tutor dashboard)
export const BrowseClassesButton = styled.button`
  background: ${COLORS.primary};
  color: white;
  border: none;
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;
  margin-bottom: 20px;
  
  &:hover {
    background: ${COLORS.darkPurple};
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(61, 42, 112, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

export const ShowMoreButton = styled.button`
  background: none;
  border: none;
  color: ${COLORS.primary};
  font-size: 0.85rem;
  font-weight: 500;
  cursor: pointer;
  padding: 10px;
  width: 100%;
  text-align: center;
  margin-top: 12px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  
  &:hover {
    color: ${COLORS.darkPurple};
    background: #f8fafc;
    border-radius: 6px;
  }
`;

export const ViewAllLink = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  background: none;
  border: none;
  color: ${COLORS.primary};
  font-size: 0.85rem;
  font-weight: 500;
  text-decoration: none;
  cursor: pointer;
  padding: 4px 8px;
  transition: all 0.2s ease;
  white-space: nowrap;
  flex-shrink: 0;
  
  &:hover {
    color: ${COLORS.darkPurple};
    gap: 8px;
  }
`;

export const ArrowIcon = styled.span`
  transition: transform 0.2s ease;
  ${ViewAllLink}:hover & {
    transform: translateX(2px);
  }
`;

export const EmptyBlockState = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  text-align: center;
  padding: 30px;
`;

export const EmptyIcon = styled.div`
  font-size: 64px;
  margin-bottom: 16px;
  opacity: 0.5;
`;

export const EmptyText = styled.p`
  font-size: 1.25rem;
  color: #334155;
  margin-bottom: 8px;
  font-weight: 600;
`;

export const EmptySubText = styled.p`
  font-size: 1rem;
  color: #64748b;
  margin-bottom: 24px;
`;

// Today's Schedule Section
export const TodayScheduleSection = styled.div`
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
`;

export const TodayScheduleHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

export const TodayScheduleTitle = styled.h3`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin: 0;
  display: flex;
  align-items: center;
  gap: 8px;
`;

export const ScheduleIcon = styled.span`
  font-size: 20px;
`;

export const TodayDate = styled.span`
  font-size: 13px;
  color: #64748b;
  font-weight: 500;
`;

export const TodayScheduleList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const ScheduleArrow = styled.div`
  color: #94a3b8;
  font-size: 12px;
  transition: all 0.2s ease;
`;

export const TodayScheduleItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  padding: 12px;
  background: white;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  align-items: center;
  border: 1px solid #e2e8f0;
  
  &:hover {
    background: #f3f4f6;
    transform: translateX(4px);
    border-color: ${COLORS.primary};
    
    ${ScheduleArrow} {
      color: ${COLORS.primary};
      transform: translateX(2px);
    }
  }
`;

export const ScheduleTime = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${COLORS.primary};
  min-width: 60px;
`;

export const ScheduleDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const ScheduleClassName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ScheduleSubject = styled.div`
  font-size: 12px;
  color: #64748b;
`;

export const TodayScheduleEmpty = styled.div`
  text-align: center;
  padding: 24px;
  color: #64748b;
`;

export const EmptyScheduleIcon = styled.div`
  font-size: 36px;
  margin-bottom: 8px;
  opacity: 0.5;
`;

export const EmptyScheduleText = styled.p`
  font-size: 14px;
  margin: 0;
`;

// Loading Block
export const LoadingBlock = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 100px;
`;

// Dashboard Container
export const DashboardContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 20px;
  background-color: #f8fafc;
  min-height: 100vh;
  
  @media (max-width: 768px) {
    padding: 16px;
  }
`;

export const ErrorText = styled.p`
  font-size: 16px;
  color: ${COLORS.errorRed || '#dc3545'};
  text-align: center;
  padding: 40px 20px;
  background-color: white;
  border-radius: 12px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  margin: 20px auto;
  max-width: 500px;
`;

// Legacy styles (keeping for backward compatibility with existing components)
export const Container = styled.div`
  min-height: 100vh;
`;

export const ContentContainer = styled.div`
  padding: ${({ theme }) => theme.spacing.md};
`;

export const Banner = styled.img`
  display: block;
  margin: ${({ theme }) => theme.spacing.sm} auto;
  border-radius: ${({ theme }) => theme.radii.xxl};
  width: 100%;
  max-height: 200px;
  object-fit: contain;
`;

export const CategoryTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  margin-bottom: ${({ theme }) => theme.spacing.xs};
  color: ${({ theme }) => theme.colors.textDark};
`;

export const CategoriesScroll = styled.div`
  overflow-x: auto;
`;

export const CategoriesContainer = styled.div`
  display: flex;
  flex-direction: row;
  gap: ${({ theme }) => theme.spacing.xs};
`;

export const CategoryTag = styled.button`
  border: 1px solid ${({ theme }) => theme.colors.primary};
  padding: ${({ theme }) => theme.spacing.xs} ${({ theme }) => theme.spacing.xs};
  border-radius: ${({ theme }) => theme.radii.md};
  background-color: transparent;
  cursor: pointer;
  font-size: ${({ theme }) => theme.fontSizes.xs};
  color: ${({ theme }) => theme.colors.primary};
  white-space: nowrap;
  
  &:hover {
    background-color: ${({ theme }) => theme.colors.primary};
    color: ${({ theme }) => theme.colors.white};
  }
`;

export const DashboardContentWrapper = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.md};
  max-width: 100vw;
  overflow-x: hidden;
  
  @media (min-width: ${({ theme }) => theme.breakpoints.md}) {
    padding: 0 ${({ theme }) => theme.spacing.xl};
  }
`;

export const LegacySectionTitle = styled.h2`
  font-size: ${({ theme }) => theme.fontSizes.lg};
  font-weight: ${({ theme }) => theme.fontWeights.bold};
  color: ${({ theme }) => theme.colors.textDark};
  margin: ${({ theme }) => theme.spacing.xl} 0 ${({ theme }) => theme.spacing.md} 0;
  padding-left: ${({ theme }) => theme.spacing.sm};
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    padding-left: 0;
    text-align: center;
  }
`;

export const HeaderSection = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: ${({ theme }) => theme.spacing.xl} 0 ${({ theme }) => theme.spacing.md} 0;
  padding: 0 ${({ theme }) => theme.spacing.sm};
  
  ${LegacySectionTitle} {
    margin: 0;
  }
  
  @media (max-width: ${({ theme }) => theme.breakpoints.md}) {
    flex-direction: column;
    gap: ${({ theme }) => theme.spacing.md};
    padding: 0;
  }
`;

export const SearchButton = styled.button`
  background: ${({ theme }) => theme.colors.primary};
  color: white;
  border: none;
  border-radius: ${({ theme }) => theme.radii.lg};
  padding: ${({ theme }) => theme.spacing.sm} ${({ theme }) => theme.spacing.lg};
  font-size: ${({ theme }) => theme.fontSizes.md};
  font-weight: ${({ theme }) => theme.fontWeights.medium};
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing.xs};
  transition: all 0.2s ease;
  
  &:hover {
    background: ${({ theme }) => theme.colors.darkPurple};
    transform: translateY(-2px);
    box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);
  }
  
  &:active {
    transform: translateY(0);
  }
`;

export const SliderWrapper = styled.div`
  margin: 0 -${({ theme }) => theme.spacing.sm};
  padding-bottom: ${({ theme }) => theme.spacing.lg};
  position: relative;
`;

export const SlideItemWrapper = styled.div`
  padding: 0 ${({ theme }) => theme.spacing.sm};
  box-sizing: border-box;
  height: 100%;
  display: flex !important;
  min-width: ${({ theme }) => theme.layout.cardMinWidth};
  
  & > div {
    margin: 0 auto;
    width: 100%;
    height: 100%;
    min-width: ${({ theme }) => theme.layout.cardContentMinWidth};
  }
`;

// Upcoming Classes Section Styles
export const UpcomingClassesSection = styled.div`
  background: #ffffff;
  border-radius: 12px;
  padding: 16px;
  margin-bottom: 20px;
  border: 1px solid #e2e8f0;
`;

export const UpcomingClassesList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const UpcomingClassItem = styled.div`
  display: grid;
  grid-template-columns: auto 1fr auto;
  gap: 12px;
  padding: 12px;
  background: #f8fafc;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  align-items: center;
  border: 1px solid #e2e8f0;
  
  &:hover {
    background: #f3f4f6;
    transform: translateX(4px);
    border-color: ${COLORS.primary};
    
    .schedule-arrow {
      color: ${COLORS.primary};
      transform: translateX(2px);
    }
  }
`;

export const DateBadge = styled.div`
  background: ${COLORS.primary};
  color: white;
  border-radius: 8px;
  padding: 8px 12px;
  text-align: center;
  min-width: 60px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 2px;
`;

export const DateDay = styled.div`
  font-size: 12px;
  font-weight: 500;
  text-transform: uppercase;
`;

export const DateMonthDay = styled.div`
  font-size: 14px;
  font-weight: 700;
`;

export const UpcomingClassDetails = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

export const UpcomingClassName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const UpcomingClassMeta = styled.div`
  font-size: 12px;
  color: #64748b;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const UpcomingClassesEmpty = styled.div`
  text-align: center;
  padding: 24px;
  color: #64748b;
`;

// Classes Grid - for displaying class items in a list
export const ClassesGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ClassGridItem = styled.div`
  width: 100%;
  transition: transform 0.2s ease;
  
  &:hover {
    transform: translateY(-4px);
  }
`;

export const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  flex-wrap: wrap;
`;
